













































































import { mapGetters } from 'vuex';

import { AxiosError } from 'axios';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import {
  downloadRequestStreamRecordItem,
  getStreamRecordingsList
} from '@/api/CopyStake/StreamRecordings';
import {
  EStreamRecordDownloadStatus,
  EStreamRecordStatus,
  TStreamRecord
} from '@/api/schema';
import { DataTableHeader } from 'vuetify';
import { doDownload } from '@/helpers/download';
import {
  DOWNLOAD_STATUS_POLLING_INTERVAL,
  formatDuration,
  isDisabledDownloadRecordStatus,
  isPreparingDownloadStatus,
  isReadyDownloadStatus,
  isReadyRequestDownloadStatus
} from '@/views/CopyStake/CopyStakeStreamRecordings/CopyStakeStreamRecordingsHelper';

type TStreamRecordingsTabState = {
  streamRecordingsList: TStreamRecord[];
  selectedItem: TStreamRecord | null;
  streamRecordingsListFilters: IFilterData | null;
  showStreamRecordPlayerModal: boolean;
  options: {
    page: number;
    itemsPerPage: number;
  };
  total: number | null;
  EStreamRecordStatus: typeof EStreamRecordStatus;
  loading: Record<string, boolean>;
  downloadStatusPollingInterval: number | null;
  listRequestToken: number;
};

export interface IFilterData {
  searchBy: string;
  searchPhrase: string;
}

const defaultOptions = {
  page: 1,
  itemsPerPage: 10
};

const CopyStakeStreamRecordingsFilter = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeStreamRecordings/CopyStakeStreamRecordingsFilter.vue'
  );
const CopyStakeStreamRecordPlayerModal = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeStreamRecordings/CopyStakeStreamRecordPlayerModal.vue'
  );
const CopyAddress = (): Promise<any> =>
  import('@/components/CopyAddress.vue');
const Tooltip = (): Promise<any> =>
  import('@/components/tooltip/Tooltip.vue');

export default {
  name: 'CopyStakeStreamRecordingsTable',
  components: {
    CopyStakeStreamRecordingsFilter,
    CopyStakeStreamRecordPlayerModal,
    CopyAddress,
    Tooltip
  },
  data(): TStreamRecordingsTabState {
    return {
      streamRecordingsList: [],
      selectedItem: null,
      streamRecordingsListFilters: null,
      showStreamRecordPlayerModal: false,
      total: null,
      EStreamRecordStatus,
      options: { ...defaultOptions },
      loading: {
        list: false
      },
      downloadStatusPollingInterval: null,
      listRequestToken: 0
    };
  },
  watch: {
    operatorId: {
      handler(): void {
        if (this.operatorId !== null) {
          this.getStreamRecordingsList();
        }
      },
      immediate: true
    },
    isHaveRecordWithPreparingDownloadStatus: {
      handler(): void {
        this.updateDownloadStatusPolling();
      },
      immediate: true
    }
  },
  beforeDestroy(): void {
    this.stopDownloadStatusPolling();
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    isLoading(): boolean {
      return Object.values(this.loading).some(Boolean);
    },
    tableHeaders(): DataTableHeader[] {
      return [
        { text: 'Start Time/End Time', value: 'startTime' },
        { text: 'Duration', value: 'duration' },
        { text: 'Operator User ID', value: 'operatorUserId' },
        { text: 'Streamer Nickname', value: 'nickName' },
        { text: 'Status', value: 'status' },
        { text: 'Action', value: 'actions', width: 80 }
      ];
    },
    statusMap(): any {
      return {
        [EStreamRecordStatus.INITIALIZED]: {
          text: 'Initialized',
          color: '#00aaff'
        },
        [EStreamRecordStatus.RECORDING]: {
          text: 'Recording',
          color: '#1677FF'
        },
        [EStreamRecordStatus.COMPLETED]: {
          text: 'Completed',
          color: '#26AD60'
        },
        [EStreamRecordStatus.ERROR]: {
          text: 'Recording Failed',
          color: '#EC5A58',
          tooltipText: 'Error Code: 500 Internal Server Error',
          icon: require('@/assets/images/icons/circle-loading.svg')
        }
      };
    },
    disabledPlayStreamRecordStatuses(): EStreamRecordStatus[] {
      return [EStreamRecordStatus.INITIALIZED, EStreamRecordStatus.ERROR];
    },
    isHaveRecordWithPreparingDownloadStatus(): boolean {
      return this.streamRecordingsList.some((streamRecord: TStreamRecord) =>
        isPreparingDownloadStatus(streamRecord.downloadStatus)
      );
    }
  },
  methods: {
    handleUpdateFilter(filters: IFilterData | null): void {
      this.streamRecordingsListFilters = filters || null;

      this.getStreamRecordingsList();
    },
    async getStreamRecordingsList(showLoading = true): Promise<void> {
      if (!this.operatorId) return;

      const token = ++this.listRequestToken;

      if (showLoading) {
        this.loading.list = true;
      }

      const filters = this.streamRecordingsListFilters
        ? {
            [this.streamRecordingsListFilters.searchBy]:
              this.streamRecordingsListFilters.searchPhrase
          }
        : null;
      const params = {
        size: this.options.itemsPerPage,
        page: this.options.page - 1
      };

      getStreamRecordingsList({ ...params, ...filters })
        .then(({ content, totalElements }) => {
          if (token !== this.listRequestToken) return;

          this.streamRecordingsList = content;
          this.total = totalElements;

          if (this.selectedItem) {
            const refreshed = this.streamRecordingsList.find(
              (item: TStreamRecord) => item.id === this.selectedItem.id
            );
            if (refreshed) {
              this.selectedItem = refreshed;
            }
          }
        })
        .catch((error: AxiosError) => {
          if (token !== this.listRequestToken) return;

          if (showLoading) {
            errorToastMessage(error);
            this.streamRecordingsList = [];
          }
        })
        .finally(() => {
          if (token === this.listRequestToken && showLoading) {
            this.loading.list = false;
          }
        });
    },
    async getRequestStreamRecordDownloadUrl(
      streamRecord: TStreamRecord
    ): Promise<void> {
      if (!streamRecord) return;

      const originalItem = this.streamRecordingsList.find(
        (item: TStreamRecord) => item.id === streamRecord.id
      );

      if (!originalItem) return;

      this.$set(
        originalItem,
        'downloadStatus',
        EStreamRecordDownloadStatus.PREPARING
      );

      downloadRequestStreamRecordItem(streamRecord.id)
        .then(({ downloadStatus, downloadUrl }) => {
          const freshItem = this.streamRecordingsList.find(
            (item: TStreamRecord) => item.id === streamRecord.id
          );
          if (!freshItem) return;
          this.$set(freshItem, 'downloadStatus', downloadStatus);
          this.$set(freshItem, 'downloadUrl', downloadUrl);
        })
        .catch((error: AxiosError) => {
          errorToastMessage(error);
        });
    },
    updateDownloadStatusPolling(): void {
      if (this.isHaveRecordWithPreparingDownloadStatus) {
        this.startDownloadStatusPolling();
      } else {
        this.stopDownloadStatusPolling();
      }
    },
    startDownloadStatusPolling(): void {
      if (this.downloadStatusPollingInterval !== null) return;

      this.downloadStatusPollingInterval = window.setInterval(() => {
        this.getStreamRecordingsList(false);
      }, DOWNLOAD_STATUS_POLLING_INTERVAL);
    },
    stopDownloadStatusPolling(): void {
      if (this.downloadStatusPollingInterval === null) return;

      window.clearInterval(this.downloadStatusPollingInterval);
      this.downloadStatusPollingInterval = null;
    },
    formatDuration,
    openStreamRecordPlayerModal(streamRecord: TStreamRecord): void {
      if (!streamRecord) return;

      this.selectedItem = streamRecord;

      this.showStreamRecordPlayerModal = true;
    },
    handlePlayStreamRecord(streamRecord: TStreamRecord): void {
      if (!streamRecord) return;

      this.openStreamRecordPlayerModal(streamRecord);
    },
    handleDownloadStreamRecordClick(streamRecord: TStreamRecord): void {
      if (!streamRecord) return;

      if (
        isReadyDownloadStatus(streamRecord.downloadStatus) &&
        streamRecord.downloadUrl
      ) {
        this.handleDownloadStreamRecord(streamRecord);
        return;
      }

      if (
        isReadyRequestDownloadStatus(streamRecord) ||
        isReadyDownloadStatus(streamRecord.downloadStatus)
      ) {
        this.getRequestStreamRecordDownloadUrl(streamRecord);
      }
    },
    handleDownloadStreamRecord(streamRecord: TStreamRecord): void {
      if (!streamRecord?.downloadUrl) return;

      doDownload(
        streamRecord.downloadUrl,
        `stream-recording-${streamRecord.nickName}-${streamRecord.id}.mp4`
      );
    },
    getDownloadStreamRecordBtnDisabled(streamRecord: TStreamRecord): boolean {
      return (
        isPreparingDownloadStatus(streamRecord.downloadStatus) ||
        isDisabledDownloadRecordStatus(streamRecord.status)
      );
    },
    getDownloadStreamRecordBtnTooltipText(
      streamRecord: TStreamRecord
    ): string {
      if (isDisabledDownloadRecordStatus(streamRecord.status)) {
        return 'Unable to download';
      }

      if (streamRecord.status !== EStreamRecordStatus.COMPLETED) {
        return '';
      }

      if (isReadyRequestDownloadStatus(streamRecord)) {
        return 'Request download';
      }

      if (isPreparingDownloadStatus(streamRecord.downloadStatus)) {
        return (
          'The recording will be<br>prepared for download.<br>' +
          'This may take a few minutes.'
        );
      }

      if (isReadyDownloadStatus(streamRecord.downloadStatus)) {
        return 'Video Ready - click to download';
      }

      return '';
    },
    getDownloadStreamRecordBtnIcon(streamRecord: TStreamRecord): string {
      return isPreparingDownloadStatus(streamRecord.downloadStatus)
        ? 'mdi-arrow-down'
        : 'mdi-tray-arrow-down';
    },
    getDownloadStreamRecordBtnIconClass(streamRecord: TStreamRecord): string {
      if (isReadyDownloadStatus(streamRecord.downloadStatus)) {
        return 'stream-recordings-table__download-icon--ready';
      }

      if (isPreparingDownloadStatus(streamRecord.downloadStatus)) {
        return 'stream-recordings-table__download-icon--progress';
      }

      return '';
    },
    getDownloadStreamRecordBtnParams(streamRecord: TStreamRecord): any {
      if (!streamRecord) return {};

      return {
        disabled: this.getDownloadStreamRecordBtnDisabled(streamRecord),
        tooltipText: this.getDownloadStreamRecordBtnTooltipText(streamRecord),
        icon: this.getDownloadStreamRecordBtnIcon(streamRecord),
        class: this.getDownloadStreamRecordBtnIconClass(streamRecord)
      };
    }
  }
};
